// features/judge-assist/JudgeAssistWidget.tsx
//
// Floating chat-bubble button (bottom-right). Tap it to open the Judge Assist
// panel. Rendered once, globally, as a sibling of the route tree in App.tsx.
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Badge, Box, Fab, IconButton, Paper, Slide, Switch, Tooltip, Typography, alpha } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { onValue, ref, set } from 'firebase/database';
import { useLocation, matchPath } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { auth, db } from '../../config/firebase';
import { allRoutes } from '../../routes/config';
import { JudgeAssistChat } from './JudgeAssistChat';
import { RulingsManager } from './RulingsManager';
import { ChatPanel } from '../chat/pages/ChatPage';

// Realtime Database path for the admin on/off flag.
const ENABLED_FLAG = 'config/judgeAssist/enabled';

// ── Who can see the Judge Assist bubble ────────────────────────────────
// One switch to widen the audience. Going public is a one-line change here.
//   'store-admin' → store owners + admins only   (current)
//   'staff'       → any event staff (HJ, FJ, scorekeeper, …)  ← flip here later
//   'authed'      → any logged-in user
//   'public'      → everyone, including logged-out visitors
// NOTE: regardless of this setting, the bubble ONLY renders on the staff /
// admin dashboard (auth: 'protected' routes) — never on the player portal.
type Audience = 'store-admin' | 'staff' | 'authed' | 'public';
const AUDIENCE: Audience = 'store-admin';

export function JudgeAssistWidget(): ReactElement | null {
  const { user, isAdmin, isStoreOwner, isStaff } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Bump to wipe the conversation and start a fresh question (remounts the chat).
  const [resetKey, setResetKey] = useState(0);
  // Admin-only: show the rulings manager instead of the chat.
  const [manage, setManage] = useState(false);

  // Pre-warm the prompt cache the first time the panel opens, so the user's
  // first question reads the cached prefix instead of paying a cold write.
  const warmedRef = useRef(false);
  useEffect(() => {
    if (!open || warmedRef.current) return;
    warmedRef.current = true;
    void auth.currentUser
      ?.getIdToken()
      .then((token) =>
        fetch('/api/judge/warm', { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
      )
      .catch(() => {
        /* best-effort; the first real question will warm it anyway */
      });
  }, [open]);

  // Global on/off flag, controlled by admins, stored in Realtime Database.
  // Defaults to ON when unset. Admins always keep access (so they can flip it);
  // everyone else only sees the bubble while it's ON.
  const [enabled, setEnabled] = useState(true);
  useEffect(() => {
    const flagRef = ref(db, ENABLED_FLAG);
    const unsub = onValue(
      flagRef,
      (snap) => {
        const v = snap.val();
        setEnabled(v === null || v === undefined ? true : Boolean(v));
      },
      () => setEnabled(true), // read denied / offline → fail open
    );
    return () => unsub();
  }, []);

  const admin = isAdmin();
  const allowed =
    AUDIENCE === 'public'
      ? true
      : AUDIENCE === 'authed'
        ? Boolean(user)
        : AUDIENCE === 'staff'
          ? isStaff()
          : /* 'store-admin' */ admin || isStoreOwner();

  // Staff dashboard only: render only on 'protected' (staff/admin/tournament)
  // routes — never on the player portal or public/guest pages.
  const matchedRoute = allRoutes.find((r) => matchPath({ path: r.path, end: true }, location.pathname));
  const onStaffRoute = matchedRoute?.auth === 'protected';

  // Judge Assist bubble: audience + global enable flag (admins keep it to toggle).
  const showJudgeAssist = onStaffRoute && allowed && (enabled || admin);
  // Staff chat bubble: any staff member on a staff route. Independent of the
  // Judge Assist toggle, so chat is always reachable even when JA is off.
  const showChat = onStaffRoute && Boolean(user);

  if (!showJudgeAssist && !showChat) return null;

  return (
    <>
      {/* Staff chat bubble — sits above the Judge Assist bubble when both show. */}
      {showChat && !open && !chatOpen && (
        <Tooltip title="Staff Chat" placement="left">
          <Fab
            color="success"
            onClick={() => setChatOpen(true)}
            aria-label="Open Staff Chat"
            sx={{
              position: 'fixed',
              // On mobile, clear the fixed bottom quick-actions bar (~72px);
              // on desktop there's no bar so sit at the normal offset. When the
              // Judge Assist bubble is also shown, stack 72px above it.
              bottom: showJudgeAssist
                ? { xs: 'calc(env(safe-area-inset-bottom, 0px) + 160px)', sm: 'calc(env(safe-area-inset-bottom, 0px) + 88px)' }
                : { xs: 'calc(env(safe-area-inset-bottom, 0px) + 88px)', sm: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' },
              right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
              zIndex: (t) => t.zIndex.drawer + 2,
              boxShadow: (t) => `0 0 20px ${alpha(t.palette.success.main, 0.5)}`,
            }}
          >
            <ChatBubbleOutlineIcon />
          </Fab>
        </Tooltip>
      )}

      {/* The Judge Assist bubble */}
      {showJudgeAssist && !open && !chatOpen && (
        <Tooltip title={admin && !enabled ? 'Judge Assist (off for staff)' : 'Judge Assist'} placement="left">
          <Fab
            color="primary"
            onClick={() => setOpen(true)}
            aria-label="Open Judge Assist"
            sx={{
              position: 'fixed',
              // On mobile, sit above the fixed bottom quick-actions bar (~72px);
              // on desktop (no bar) use the normal offset. Safe-area insets keep
              // it clear of the iOS home bar either way.
              bottom: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 88px)', sm: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' },
              right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
              zIndex: (t) => t.zIndex.drawer + 2,
              opacity: admin && !enabled ? 0.55 : 1,
              boxShadow: (t) => `0 0 20px ${alpha(t.palette.primary.main, 0.5)}`,
            }}
          >
            <GavelIcon />
          </Fab>
        </Tooltip>
      )}

      {/* The Judge Assist panel */}
      <Slide direction="up" in={open && showJudgeAssist} mountOnEnter unmountOnExit>
        <Paper
          elevation={16}
          sx={{
            position: 'fixed',
            bottom: { xs: 0, md: 24 },
            right: { xs: 0, md: 24 },
            zIndex: (t) => t.zIndex.drawer + 2,
            width: { xs: '100%', sm: 400 },
            height: { xs: '85vh', sm: '70vh' },
            maxHeight: 720,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: { xs: '16px 16px 0 0', md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Badge color="success" variant="dot" overlap="circular">
              <GavelIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.5 }}>
                JUDGE ASSIST
              </Typography>
              <Typography sx={{ fontSize: 9.5, color: 'text.disabled', letterSpacing: 1 }}>
                RULES &amp; PROCEDURE SUPPORT
              </Typography>
            </Box>

            {admin && (
              <Tooltip title={enabled ? 'ON for staff — tap to disable' : 'OFF for staff — tap to enable'}>
                <Switch
                  size="small"
                  checked={enabled}
                  onChange={(e) => void set(ref(db, ENABLED_FLAG), e.target.checked)}
                  inputProps={{ 'aria-label': 'Toggle Judge Assist for staff' }}
                />
              </Tooltip>
            )}
            {admin && (
              <Tooltip title={manage ? 'Back to chat' : 'Manage rulings'}>
                <IconButton
                  size="small"
                  onClick={() => setManage((m) => !m)}
                  aria-label="Manage rulings"
                  sx={{ color: manage ? 'primary.main' : 'text.secondary' }}
                >
                  <MenuBookIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {!manage && (
              <Tooltip title="New question">
                <IconButton
                  size="small"
                  onClick={() => setResetKey((k) => k + 1)}
                  aria-label="Start a new question"
                >
                  <RestartAltIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Judge Assist">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Body: chat, or (admins) the rulings manager */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {manage ? <RulingsManager /> : <JudgeAssistChat key={resetKey} />}
          </Box>

          <Typography
            sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', py: 0.5, px: 1 }}
          >
            Staff tool · AI-generated — verify critical rulings with the Head Judge
          </Typography>
        </Paper>
      </Slide>

      {/* The staff chat panel — unfolds in place like a CS chat widget. */}
      <Slide direction="up" in={chatOpen && showChat} mountOnEnter unmountOnExit>
        <Paper
          elevation={16}
          sx={{
            position: 'fixed',
            bottom: { xs: 0, md: 24 },
            right: { xs: 0, md: 24 },
            zIndex: (t) => t.zIndex.drawer + 2,
            width: { xs: '100%', sm: 400 },
            height: { xs: '85vh', sm: '70vh' },
            maxHeight: 720,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: { xs: '16px 16px 0 0', md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.default',
          }}
        >
          <ChatPanel user={user} onClose={() => setChatOpen(false)} />
        </Paper>
      </Slide>
    </>
  );
}
